import React, { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useCollections } from "@/lib/api/hooks/useCollections"
import { usePicks } from "@/lib/api/hooks/usePicks"
import { useAuthStore } from "@/lib/store/useAuthStore"
import { Preferences } from "@capacitor/preferences"
import { useAppConfig } from "@/lib/api/hooks/useAppConfig"

export function OnboardingGate({ children }: { children: React.ReactNode }) {
    const { user } = useAuthStore()
    const { data: collections, isLoading: isCollLoading } = useCollections()
    const { data: picksData, isLoading: isPicksLoading } = usePicks({})
    const { data: config } = useAppConfig()
    const location = useLocation()
    const navigate = useNavigate()
    const [hasSeenOnboarding, setHasSeenOnboarding] = useState<boolean | null>(null)

    // Determine if the user's account was created in the last 2 minutes
    const isNewUser = user?.created_at 
        ? Date.now() - new Date(user.created_at).getTime() < 120000 
        : false

    const userTermsVersion = user?.user_metadata?.terms_accepted_version
    const oldTermsAccepted = user?.user_metadata?.terms_accepted === true
    
    // Treat the legacy true flag as equivalent to "1.0"
    const effectiveVersion = userTermsVersion || (oldTermsAccepted ? "1.0" : null)

    const CURRENT_LEGAL_VERSION = config?.current_legal_version
    
    // If the config hasn't loaded yet, assume they have the correct terms to avoid blocking render or throwing them into an infinite loop
    const hasCurrentTerms = !CURRENT_LEGAL_VERSION || effectiveVersion === CURRENT_LEGAL_VERSION
    const needsReconsent = !!effectiveVersion && !!CURRENT_LEGAL_VERSION && effectiveVersion !== CURRENT_LEGAL_VERSION

    useEffect(() => {
        // Step 0 Legal Trap: Forcefully trap un-consented users immediately.
        // Even if they somehow bypassed it previously, they cannot proceed until they accept.
        if (user && !hasCurrentTerms) {
            const destination = needsReconsent ? '/onboarding?reconsent=true' : '/onboarding'
            if (location.pathname !== '/onboarding' && location.pathname + location.search !== destination) {
                navigate(destination, { replace: true })
            }
            return // Halt all other onboarding checks while trapped
        }

        if (!isCollLoading && !isPicksLoading && hasSeenOnboarding !== null) {
            if (hasSeenOnboarding) return // Skip completely if they've already onboarded
            
            const hasCollections = (collections?.collections?.length || 0) > 0
            const hasPicks = (picksData?.pages?.[0]?.picks?.length || 0) > 0
            
            // If they happen to have collections already, save the preference silently to skip future checks!
            if (hasCollections || hasPicks) {
                Preferences.set({ key: 'has_onboarded', value: 'true' })
                setHasSeenOnboarding(true)
                if (location.pathname === '/onboarding') {
                    navigate('/', { replace: true })
                }
                return
            }

            if (!hasCollections && !hasPicks && location.pathname !== '/onboarding') {
                navigate('/onboarding', { replace: true })
            }
        }
    }, [isCollLoading, isPicksLoading, collections, picksData, location.pathname, navigate, hasSeenOnboarding, hasCurrentTerms, user])

    // Prevent skeleton flicker for brand new users by showing a simple loading state 
    // while we query the database to confirm they have 0 collections.
    if (isNewUser && (isCollLoading || isPicksLoading) && location.pathname !== '/onboarding') {
        return (
            <div className="flex h-[100dvh] w-full items-center justify-center bg-background">
                <span className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
        )
    }

    return <>{children}</>
}
